import React, { memo, useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
    Grid,
    IconButton,
    Typography,
    NativeSelect,
    InputBase,
} from '@mui/material'
import { translate } from '../i18n'
import { LeftIcon, RightIcon } from '../components/Icons'
import { PaginationFooterProps } from './Pagination.types'

function PaginationFooter({
    page,
    pageSize,
    count,
    total,
    sizes = [20, 50, 100],
    skipPushToHistory,
    onChangedPage,
    onChangedPageSize,
}: PaginationFooterProps) {
    const history = useHistory()
    const location = useLocation()
    const query = useMemo(
        () => new URLSearchParams(location.search),
        [location]
    )

    const from = useMemo(
        () => pageSize * (page - 1) + (count === 0 ? 0 : 1),
        [pageSize, page, count]
    )
    const to = useMemo(
        () => pageSize * (page - 1) + count,
        [pageSize, page, count]
    )
    const textStyle = useMemo(
        () => ({
            fontSize: '0.875rem',
            fontWeight: 500,
        }),
        []
    )

    const updateRoute = useCallback(() => {
        if (!skipPushToHistory) {
            history.push({
                pathname: location.pathname,
                state: location.state,
                search: query.toString(),
            })
        }
    }, [skipPushToHistory, history, location, query])

    const changedPageSize = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            query.set('page', '1')
            query.set('pageSize', event.target.value)

            updateRoute()
            onChangedPageSize(1, parseInt(event.target.value))
        },
        [query, updateRoute, onChangedPageSize]
    )

    const nextPage = useCallback(() => {
        query.set('page', (page + 1).toString())

        updateRoute()
        onChangedPage(page + 1)
    }, [query, page, updateRoute, onChangedPage])

    const previousPage = useCallback(() => {
        query.set('page', (page - 1).toString())

        updateRoute()
        onChangedPage(page - 1)
    }, [query, page, updateRoute, onChangedPage])

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs="auto">
                <NativeSelect
                    onChange={changedPageSize}
                    value={pageSize}
                    input={<InputBase />}
                    sx={textStyle}
                >
                    {sizes.map((el) => (
                        <option key={el} value={el}>
                            {el}
                        </option>
                    ))}
                </NativeSelect>
            </Grid>
            <Grid item xs="auto">
                <Grid container justifyContent="flex-start" alignItems="center">
                    <Grid item xs="auto">
                        <Typography
                            component="span"
                            variant="body2"
                            sx={textStyle}
                        >
                            {translate('pagination', {
                                from: from,
                                to: to,
                                total,
                            })}
                        </Typography>
                    </Grid>
                    <Grid item xs="auto">
                        <IconButton
                            aria-label="Previous page"
                            onClick={previousPage}
                            disabled={page === 1}
                        >
                            <LeftIcon />
                        </IconButton>
                        <IconButton
                            aria-label="Next page"
                            onClick={nextPage}
                            disabled={count === 0 || count < pageSize}
                        >
                            <RightIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default memo(PaginationFooter)
